import { useAppSelector } from '@/shared/hooks/store';

import { selectOnboardingUser } from '@/store/slices/onboarding/selectors';
import { InsuranceFindTypes } from '../constants';
import { useTheme } from '@mui/material/styles';
import { Box, Alert } from '@/shared/ui';
import { InsuranceManualForm } from '../components/manual-form';
import { InsuranceListItemSkeleton } from '../components/list-item-skeleton';
import { useEffect, useState } from 'react';
import { SplitLayout } from '@/modules/onboarding/components/split-layout';
import { setPageType } from '@/shared/helpers/page-type';
import { CustomersInsuranceResponse } from '@/shared/endpoints/customers';
export interface IInsuranceManualFormProps {
    onBack(findType?: InsuranceFindTypes): void;
    onSuccess?(insurance: CustomersInsuranceResponse): void;
    forceBoxed?: boolean;
}


const GetAccuratePricingUpFront = ({ forceBoxed }: { forceBoxed?: boolean }) => {
    return (
        <div className={`flex flex-col gap-[6px] xl:gap-[8px] ${forceBoxed ? 'mb-[24px] xl:mb-[35px]' : 'mb-[32px]'}`}>
            <span className='font-atkinsonBold font-bold text-18 xl:text-21 tracking-[0.42px] leading-[130%] tracking-[0.36px] text-black'>Get accurate pricing upfront</span>
            <span className='font-atkinsonHyperlegible text-15 xl:text-18 leading-[131%] xl:leading-[130%] text-primary-900'>Providing accurate insurance details helps us verify your coverage and display your correct copay before submission.</span>
        </div>
    );
}

const AddManualModalSplitLayoutSupport = ({ onBack, onSuccess, forceBoxed }: IInsuranceManualFormProps) => {
    const user = useAppSelector(selectOnboardingUser)!;
    const theme = useTheme();
    const [hintField, setHintField] = useState('');

    const _conv_page_type = 'add_manual_insurance';
    useEffect(() => {
        setPageType(_conv_page_type);
    }, [_conv_page_type]);

    return (
        <SplitLayout
            className='xl:gap-[72px] justify-between'
            customLeftClassNames={['!max-w-[430px]']}
            sideContent={
                <div
                    className="flex flex-col min-w-0 w-full h-full max-w-[100%] overflow-visible"
                    data-testid="insurance-manual-form-page"
                >
                    <h2
                        className={`insurane__upload_heading font-agrandir font-normal ${forceBoxed ? 'text-30 sm:text-50 mb-[12px] xl:mb-[32px]' : 'text-30 sm:text-40 mb-[22px] xl:mb-[32px]'} leading-[130%] xl-max:text-center`}
                        data-testid="insurance-edit-manually-title"
                    >
                        Please <span className="text-primary">Enter Your</span> <br /> <span className="text-primary">Insurance</span>{' '}
                        Details
                    </h2>

                    <div className={`flex ${forceBoxed ? 'flex-col gap-[24px]' : 'flex-col-reverse xl:flex-col gap-[12px]'} xl:gap-[16px]`}>
                        <p className="text-16 sm:text-21 leading-[130%] font-atkinsonHyperlegible text-center xl:text-start font-normal">
                            <span className="font-atkinsonBold font-bold italic">Here's an Example</span> of where you can find these details on your insurance card.
                        </p>
                        <InsuranceListItemSkeleton inModal={!forceBoxed} hintField={hintField} />
                    </div>
                    <div className={` ${forceBoxed ? 'mt-[24px] xl:mt-[48px] xl-max:mb-[24px]' : 'mt-[32px] xl:mt-[48px] xl-max:mb-[32px]'}`}>
                        <Alert removeBorder variant='infoV2' label={
                            <div className='flex flex-col gap-[.25rem]'>
                                <span className='font-atkinsonBold font-bold text-18 leading-[137%]'>Note</span>
                                <span className='font-dmSans font-normal text-16 leading-[125%]'>Incorrect or incomplete details may prevent copays from appearing and delay processing.</span>
                            </div>
                        } />
                    </div>
                </div>
            }
            mainContent={
                <div>
                    <div className={`${forceBoxed ? '' : 'hidden xl:block'}`}>
                        <Box
                            bgWhite
                            shadow
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                                maxWidth: '100%',
                                [theme.breakpoints.up('lg')]: { padding: '1.85rem 2.8rem' }
                            }}
                        >
                            <div className="flex flex-col items-center">
                                <GetAccuratePricingUpFront />
                                <InsuranceManualForm
                                    inModal={!forceBoxed}
                                    className="w-full"
                                    hasInsurance={!!user.insurances.length || !!user.insuranceUploads.length}
                                    onBack={onBack}
                                    onHintField={setHintField}
                                    onSuccess={onSuccess}
                                />
                            </div>
                        </Box>
                    </div>
                    <div className={`${forceBoxed ? 'hidden' : 'xl:hidden'}`}>
                        <div className="flex flex-col items-center">
                            <GetAccuratePricingUpFront />
                            <InsuranceManualForm
                                className="w-full"
                                hasInsurance={!!user.insurances.length || !!user.insuranceUploads.length}
                                onBack={onBack}
                                onHintField={setHintField}
                                onSuccess={onSuccess}
                            />
                        </div>
                    </div>
                </div>
            }
        />
    );
};

export default AddManualModalSplitLayoutSupport;
